import React, { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { KeyRound, User, Lock, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export default function ActivateRestaurateur() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const navigate = useNavigate();
    const [form, setForm] = useState({ username: "", password: "", confirm: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    if (!token) {
        return (
            <div className="min-h-screen bg-ink grain pt-24 pb-16 px-6 md:px-10 flex items-center justify-center" data-testid="activate-no-token">
                <div className="max-w-md w-full bg-white/5 backdrop-blur-md border border-rose-500/30 rounded-sm p-8 text-center">
                    <AlertTriangle size={32} className="mx-auto text-rose-400 mb-4" />
                    <h2 className="font-serif text-2xl text-white mb-2">Link non valido</h2>
                    <p className="text-gray-400 text-sm">Il link di attivazione che hai utilizzato non contiene un token. Contatta il supporto.</p>
                </div>
            </div>
        );
    }

    const submit = async (e) => {
        e.preventDefault();
        if (form.password !== form.confirm) {
            toast.error("Le password non coincidono");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/auth/activate-admin", {
                token, username: form.username, password: form.password,
            });
            setDone(true);
            toast.success("Account attivato! Reindirizzamento al login...");
            setTimeout(() => navigate("/admin/login"), 2200);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Errore durante l'attivazione");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="min-h-screen bg-ink grain pt-24 pb-16 px-6 md:px-10 flex items-center justify-center" data-testid="activate-done">
                <div className="max-w-md w-full bg-white/5 backdrop-blur-md border border-emerald-500/30 rounded-sm p-8 text-center">
                    <CheckCircle2 size={36} className="mx-auto text-emerald-300 mb-4" />
                    <h2 className="font-serif text-2xl text-white mb-2">Account attivato</h2>
                    <p className="text-gray-400 text-sm">Verrai reindirizzato al login...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-ink grain pt-24 pb-16 px-6 md:px-10 flex items-center justify-center" data-testid="activate-page">
            <form onSubmit={submit} className="max-w-md w-full bg-white/5 backdrop-blur-md border border-white/10 rounded-sm p-8">
                <div className="text-center mb-6">
                    <KeyRound size={28} className="mx-auto text-gold mb-3" strokeWidth={1.5} />
                    <h2 className="font-serif text-3xl text-white">Attiva il tuo account</h2>
                    <p className="text-gray-400 text-xs mt-2">Scegli username e password per accedere al pannello del ristoratore.</p>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-1.5">Username</label>
                        <div className="relative">
                            <User className="absolute left-3 top-3 text-gold/70" size={16} strokeWidth={1.5} />
                            <input type="text" required minLength={3} maxLength={40}
                                value={form.username}
                                onChange={(e) => setForm({ ...form, username: e.target.value.trim().toLowerCase() })}
                                placeholder="es. trattoriadelvicolo"
                                data-testid="activate-username"
                                className="w-full pl-10 pr-3 py-3 bg-transparent border border-white/15 rounded-sm text-white placeholder-gray-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold text-sm"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-1.5">Password (min 8 caratteri)</label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-3 text-gold/70" size={16} strokeWidth={1.5} />
                            <input type="password" required minLength={8} maxLength={80}
                                value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                                data-testid="activate-password"
                                className="w-full pl-10 pr-3 py-3 bg-transparent border border-white/15 rounded-sm text-white focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold text-sm"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-1.5">Conferma password</label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-3 text-gold/70" size={16} strokeWidth={1.5} />
                            <input type="password" required minLength={8} maxLength={80}
                                value={form.confirm}
                                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                                data-testid="activate-confirm"
                                className="w-full pl-10 pr-3 py-3 bg-transparent border border-white/15 rounded-sm text-white focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold text-sm"
                            />
                        </div>
                    </div>

                    <button type="submit" disabled={submitting}
                        data-testid="activate-submit"
                        className="w-full bg-gold text-black font-semibold py-3.5 rounded-sm hover:bg-gold-light transition-colors tracking-widest uppercase text-sm disabled:opacity-40">
                        {submitting ? "Attivazione in corso..." : "Attiva account"}
                    </button>
                </div>

                <div className="text-center mt-5">
                    <Link to="/admin/login" className="text-xs text-gray-500 hover:text-gold tracking-widest uppercase">Hai già un account? Accedi</Link>
                </div>
            </form>
        </div>
    );
}
